import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  getAccessToken,
  onSessionEnded,
  refreshSession,
  setAccessToken,
  signOut,
} from "./session";
import { io } from "socket.io-client";
import "./App.css";
import Login from "./Login";
import UserManagement from "./UserManagement";
import AdminPanel from "./AdminPanel";

const API_BASE = "/api";
const SOCKET_URL = window.location.origin;
const LOGO_URL = "/reachlink-logo.png";
const EMOJIS = [
  "😀", "😃", "😄", "😁", "😊", "😂",
  "🤣", "😍", "🥰", "😘", "😎", "🤔",
  "👍", "👎", "👏", "🙌", "🙏", "💪",
  "❤️", "💙", "💚", "💛", "💜", "🔥",
  "🎉", "✅", "❌", "⭐", "💯", "👋",
  "🙂", "😉", "😢", "😭", "😡", "🤝",
  "👌", "✌️", "🤞", "💬", "📞", "🚕",
];

// Must match MAX_SENDABLE_MEDIA_BYTES and SENDABLE_MEDIA_TYPES in the
// API's src/mediaTypes.js.
const MAX_IMAGE_BYTES = 900 * 1024;
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/3gpp"];
const ACCEPTED_MEDIA_TYPES = [
  ...ACCEPTED_IMAGE_TYPES,
  ...ACCEPTED_VIDEO_TYPES,
];

function isVideoType(contentType) {
  return (contentType || "").startsWith("video/");
}

/*
 * Shrinks a still image to a JPEG under MAX_IMAGE_BYTES.
 * GIFs are left alone so animation is kept.
 * Returns null if the image can't be made small enough.
 */
async function shrinkImage(file) {
  if (file.size <= MAX_IMAGE_BYTES) {
    return file;
  }

  if (file.type === "image/gif") {
    return null;
  }

  const bitmap = await createImageBitmap(file);
  let maxSide = 2048;

  try {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const scale = Math.min(
        1,
        maxSide / Math.max(bitmap.width, bitmap.height)
      );
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);

      const context = canvas.getContext("2d");
      // JPEG has no transparency; use white instead of black.
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.82)
      );

      if (blob && blob.size <= MAX_IMAGE_BYTES) {
        const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
        return new File([blob], name, { type: "image/jpeg" });
      }

      maxSide = Math.round(maxSide * 0.75);
    }
  } finally {
    bitmap.close();
  }

  return null;
}

function formatDate(dateStr) {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();

  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(dateStr) {
  if (!dateStr) return "";

  return new Date(dateStr).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Sidebar time: time today, "Yesterday", weekday this week, else date.
function formatListTime(dateStr) {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const dayMs = 24 * 60 * 60 * 1000;

  if (date >= startOfToday) {
    return formatTime(dateStr);
  }

  if (date >= new Date(startOfToday - dayMs)) {
    return "Yesterday";
  }

  if (date >= new Date(startOfToday - 6 * dayMs)) {
    return date.toLocaleDateString([], { weekday: "short" });
  }

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear()
      ? { year: "numeric" }
      : {}),
  });
}

const GROUP_WINDOW_MS = 5 * 60 * 1000;

// Consecutive messages from the same side, same day, a few minutes apart.
function isSameGroup(first, second) {
  if (!first || !second) return false;
  if (first.direction !== second.direction) return false;

  if (
    first.direction === "outbound" &&
    (first.sentByUserId || null) !== (second.sentByUserId || null)
  ) {
    return false;
  }

  const firstDate = new Date(first.createdAt);
  const secondDate = new Date(second.createdAt);

  return (
    firstDate.toDateString() === secondDate.toDateString() &&
    Math.abs(secondDate - firstDate) <= GROUP_WINDOW_MS
  );
}

/*
 * Inline icon set (stroke icons on a 24px grid).
 * Shapes: "c:cx,cy,r" circle, "r:x,y,w,h,rx" rect, otherwise a path.
 */
const ICONS = {
  search: ["c:11,11,7", "m20 20-3.5-3.5"],
  building: ["M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16", "M16 9h2a2 2 0 0 1 2 2v10", "M3 21h18", "M8 7h4", "M8 11h4", "M8 15h4"],
  compose: ["M12 20h9", "M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"],
  smile: [
    "c:12,12,10",
    "M8 14s1.5 2 4 2 4-2 4-2",
    "M9 9h.01",
    "M15 9h.01",
  ],
  paperclip: [
    "m21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l8.6-8.6a4 4 0 1 1 5.7 5.7l-8.6 8.6a2 2 0 0 1-2.8-2.8l8.5-8.5",
  ],
  send: ["M22 2 11 13", "M22 2 15 22l-4-9-9-4Z"],
  bell: [
    "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9",
    "M10.3 21a1.9 1.9 0 0 0 3.4 0",
  ],
  bellOff: [
    "M8.7 3A6 6 0 0 1 18 8a21 21 0 0 0 .6 5",
    "M17 17H3s3-2 3-9a4.7 4.7 0 0 1 .3-1.7",
    "M10.3 21a1.9 1.9 0 0 0 3.4 0",
    "m2 2 20 20",
  ],
  users: [
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
    "c:9,7,4",
    "M22 21v-2a4 4 0 0 0-3-3.9",
    "M16 3.1a4 4 0 0 1 0 7.8",
  ],
  logout: [
    "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4",
    "m16 17 5-5-5-5",
    "M21 12H9",
  ],
  more: ["M12 12h.01", "M19 12h.01", "M5 12h.01"],
  archive: [
    "r:2,3,20,5,1",
    "M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8",
    "M10 12h4",
  ],
  restore: ["M3 12a9 9 0 1 0 3-6.7L3 8", "M3 3v5h5"],
  trash: [
    "M3 6h18",
    "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6",
    "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
  ],
  back: ["m15 18-6-6 6-6"],
  close: ["M18 6 6 18", "m6 6 12 12"],
  alert: ["c:12,12,10", "M12 8v4", "M12 16h.01"],
  video: ["m22 8-6 4 6 4V8Z", "r:2,6,14,12,2"],
  image: [
    "r:3,3,18,18,2",
    "c:9,9,2",
    "m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21",
  ],
  file: [
    "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
    "M14 2v6h6",
  ],
  user: ["c:12,8,4", "M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"],
  chevronUp: ["m18 15-6-6-6 6"],
  message: [
    "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  ],
};

function Icon({ name, size = 18 }) {
  const shapes = ICONS[name] || [];

  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shapes.map((shape) => {
        if (shape.startsWith("c:")) {
          const [cx, cy, r] = shape.slice(2).split(",");
          return <circle key={shape} cx={cx} cy={cy} r={r} />;
        }

        if (shape.startsWith("r:")) {
          const [x, y, width, height, rx] = shape.slice(2).split(",");
          return (
            <rect
              key={shape}
              x={x}
              y={y}
              width={width}
              height={height}
              rx={rx}
            />
          );
        }

        return <path key={shape} d={shape} />;
      })}
    </svg>
  );
}

/*
 * One attachment in a message bubble. Falls back to a labeled link
 * when the file is another type or fails to load (e.g. an expired
 * carrier URL, or a video format this browser can't play).
 */
function MessageMediaItem({ item, onImageLoad }) {
  const [broken, setBroken] = useState(false);
  const contentType = item.contentType || "";
  const isImage = !contentType || contentType.startsWith("image/");
  const isVideo = isVideoType(contentType);

  if (isVideo && !broken) {
    return (
      <video
        src={item.url}
        className="message-media-video"
        controls
        playsInline
        preload="metadata"
        aria-label={item.filename || "Video"}
        onLoadedMetadata={onImageLoad}
        onError={() => setBroken(true)}
      />
    );
  }

  if (isImage && !broken) {
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer"
        className="message-media-link"
        aria-label="Open picture in a new tab"
      >
        <img
          src={item.url}
          alt={item.filename || "Picture"}
          className="message-media-image"
          loading="lazy"
          onLoad={onImageLoad}
          onError={() => setBroken(true)}
        />
      </a>
    );
  }

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noreferrer"
      className="message-media-fallback"
    >
      <Icon name={isImage ? "image" : isVideo ? "video" : "file"} size={20} />
      <span>
        <strong>
          {isImage
            ? "Picture couldn't be loaded"
            : isVideo
              ? "Video can't play here"
              : "Attachment"}
        </strong>
        <small>
          {isImage || isVideo
            ? "Open link"
            : contentType || "Open link"}
        </small>
      </span>
    </a>
  );
}

function getStatusLabel(status) {
  switch ((status || "").toLowerCase()) {
    case "queued":
      return "Queued";

    case "sending":
      return "Sending";

    case "sent":
      return "Sent";

    case "delivered":
      return "Delivered";

    case "delivery_failed":
    case "failed":
      return "Failed";

    case "received":
      return "Received";

    default:
      return status || "";
  }
}

function getPhoneNumber(conversation) {
  return conversation?.contact?.phoneNumber || "Unknown";
}

function getBusinessPhoneNumber(conversation) {
  return conversation?.phoneNumber?.number || "";
}

function getBusinessPhoneLabel(conversation) {
  return conversation?.phoneNumber?.label || "";
}

function getContactName(conversation) {
  const firstName =
    conversation?.contact?.firstName?.trim() || "";

  const lastName =
    conversation?.contact?.lastName?.trim() || "";

  return `${firstName} ${lastName}`.trim();
}

function formatPhoneNumber(phoneNumber) {
  const digits = (phoneNumber || "").replace(/\D/g, "");

  const normalized =
    digits.length === 11 && digits.startsWith("1")
      ? digits.slice(1)
      : digits;

  if (normalized.length !== 10) {
    return phoneNumber || "Unknown";
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(
    3,
    6
  )}-${normalized.slice(6)}`;
}

function getInitials(conversation) {
  const firstName =
    conversation?.contact?.firstName?.trim() || "";

  const lastName =
    conversation?.contact?.lastName?.trim() || "";

  if (firstName || lastName) {
    const firstInitial = firstName
      ? firstName.charAt(0)
      : "";

    const lastInitial = lastName
      ? lastName.charAt(0)
      : "";

    return `${firstInitial}${lastInitial}`.toUpperCase();
  }

  const number = getPhoneNumber(conversation);

  if (number === "Unknown") {
    return "?";
  }

  return number.slice(-2);
}


// =========================================================
// NOTIFICATION SOUND
// =========================================================

let notificationAudioContext = null;

function playNotificationSound() {
  try {
    const AudioContextClass =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioContextClass) {
      return;
    }

    if (!notificationAudioContext) {
      notificationAudioContext =
        new AudioContextClass();
    }

    const audioContext =
      notificationAudioContext;

    const playChime = () => {
      const now =
        audioContext.currentTime;

      const playTone = (
        frequency,
        startTime,
        duration
      ) => {
        const oscillator =
          audioContext.createOscillator();

        const gain =
          audioContext.createGain();

        oscillator.type = "sine";

        oscillator.frequency.setValueAtTime(
          frequency,
          startTime
        );

        gain.gain.setValueAtTime(
          0,
          startTime
        );

        gain.gain.linearRampToValueAtTime(
          0.18,
          startTime + 0.02
        );

        gain.gain.exponentialRampToValueAtTime(
          0.001,
          startTime + duration
        );

        oscillator.connect(gain);
        gain.connect(
          audioContext.destination
        );

        oscillator.start(startTime);
        oscillator.stop(
          startTime + duration
        );
      };

      playTone(880, now, 0.18);
      playTone(
        1175,
        now + 0.16,
        0.28
      );
    };

    if (
      audioContext.state ===
      "suspended"
    ) {
      audioContext
        .resume()
        .then(playChime)
        .catch(() => {});
    } else {
      playChime();
    }
  } catch (error) {
    console.warn(
      "Notification sound unavailable:",
      error
    );
  }
}


// =========================================================
// PUSH NOTIFICATION HELPERS
// =========================================================

async function requestNotificationPermission() {
  if (!("Notification" in window)) {
    return "unsupported";
  }

  if (
    Notification.permission ===
    "granted"
  ) {
    return "granted";
  }

  if (
    Notification.permission ===
    "denied"
  ) {
    return "denied";
  }

  return await Notification.requestPermission();
}

function urlBase64ToUint8Array(
  base64String
) {
  const padding =
    "=".repeat(
      (4 -
        (base64String.length % 4)) %
        4
    );

  const base64 =
    (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

  const rawData =
    window.atob(base64);

  return Uint8Array.from(
    [...rawData].map(
      (character) =>
        character.charCodeAt(0)
    )
  );
}

async function registerPushSubscription(
  token
) {
  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    throw new Error(
      "Push notifications are not supported in this browser."
    );
  }

  const registration =
    await navigator.serviceWorker.register(
      "/push-sw.js"
    );

  const keyResponse =
    await fetch(
      `${API_BASE}/push/public-key`,
      {
        headers: {
          Authorization:
            `Bearer ${getAccessToken()}`,
        },
      }
    );

  if (!keyResponse.ok) {
    throw new Error(
      "Could not load push notification key."
    );
  }

  const {
    publicKey,
  } = await keyResponse.json();

  let subscription =
    await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription =
      await registration.pushManager.subscribe({
        userVisibleOnly: true,

        applicationServerKey:
          urlBase64ToUint8Array(
            publicKey
          ),
      });
  }

  const saveResponse =
    await fetch(
      `${API_BASE}/push/subscribe`,
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",

          Authorization:
            `Bearer ${getAccessToken()}`,
        },

        body:
          JSON.stringify(
            subscription
          ),
      }
    );

  if (!saveResponse.ok) {
    throw new Error(
      "Could not save push subscription."
    );
  }

  return subscription;
}

async function getCurrentPushSubscription() {
  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    return null;
  }

  const registration =
    await navigator.serviceWorker.register(
      "/push-sw.js"
    );

  return await registration.pushManager.getSubscription();
}


// =========================================================
// APPLICATION
// =========================================================

function App() {
  // Set while signed in; requests read the newest token from ./session.
  const [token, setToken] =
    useState(getAccessToken);

  // Without a stored token, try the refresh cookie before showing login.
  const [checkingSession, setCheckingSession] =
    useState(() => !getAccessToken());

  const [
    currentUser,
    setCurrentUser,
  ] = useState(null);

  const [
    pushEnabled,
    setPushEnabled,
  ] = useState(false);

  const [
    pushChanging,
    setPushChanging,
  ] = useState(false);

  const [
    showUserManagement,
    setShowUserManagement,
  ] = useState(false);

  const [
    showAdminPanel,
    setShowAdminPanel,
  ] = useState(false);

  const [
    accountUsers,
    setAccountUsers,
  ] = useState([]);

  const [
    conversationScope,
    setConversationScope,
  ] = useState("all");
  
  const [
  conversationStatus,
  setConversationStatus,
] = useState("open");

// =======================================================
// SUPER ADMIN - MANAGED CLIENT ACCOUNT
// =======================================================

const [
  managedAccounts,
  setManagedAccounts,
] = useState([]);

const [
  managedAccountId,
  setManagedAccountId,
] = useState("");

  const [
    assigningConversation,
    setAssigningConversation,
  ] = useState(false);

  const [
    showNewConversation,
    setShowNewConversation,
  ] = useState(false);

  const [
    newConversationPhone,
    setNewConversationPhone,
  ] = useState("");

  const [
    newConversationFirstName,
    setNewConversationFirstName,
  ] = useState("");

  const [
    newConversationLastName,
    setNewConversationLastName,
  ] = useState("");

  const [
    creatingConversation,
    setCreatingConversation,
  ] = useState(false);

  // Business numbers an admin can start a conversation from.
  const [
    businessNumbers,
    setBusinessNumbers,
  ] = useState([]);

  const [
    newConversationNumberId,
    setNewConversationNumberId,
  ] = useState("");

  const [
    conversations,
    setConversations,
  ] = useState([]);

  const [
    activeConv,
    setActiveConv,
  ] = useState(null);

  const [
    messages,
    setMessages,
  ] = useState([]);

  const [
    input,
    setInput,
  ] = useState("");
  
  const [
  showEmojiPicker,
  setShowEmojiPicker,
] = useState(false);

const [
  selectedImage,
  setSelectedImage,
] = useState(null);

const [
  selectedImagePreview,
  setSelectedImagePreview,
] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    loadingMessages,
    setLoadingMessages,
  ] = useState(false);

  const [
    sending,
    setSending,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const messagesEndRef =
    useRef(null);

  const activeConvRef =
    useRef(null);

  const messagesContainerRef = useRef(null);
  // True while the reader is at (or near) the newest message.
  const stickToBottomRef = useRef(true);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiWrapperRef = useRef(null);
  const accountMenuRef = useRef(null);
  const conversationMenuRef = useRef(null);

  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showConversationMenu, setShowConversationMenu] =
    useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);


  // =======================================================
  // AUTH
  // =======================================================

  const handleLogin = (
    loginData
  ) => {
    setAccessToken(
      loginData.token
    );

    setToken(
      loginData.token
    );

    setCurrentUser(
      loginData.user
    );
  };

  const clearSignedInState = () => {
    setToken("");
    setCurrentUser(null);
    setConversations([]);
    setMessages([]);
    setActiveConv(null);

    setShowUserManagement(
      false
    );
    setShowAdminPanel(false);

    setAccountUsers([]);
    setConversationScope(
      "all"
    );

    setPushEnabled(false);
  };

  const handleLogout = () => {
    signOut();
    clearSignedInState();
  };

  const clearSignedInStateRef = useRef(clearSignedInState);
  clearSignedInStateRef.current = clearSignedInState;

  useEffect(() => {
    onSessionEnded(() => clearSignedInStateRef.current());
  }, []);

  useEffect(() => {
    if (!checkingSession) {
      return;
    }

    refreshSession()
      .then((session) => setToken(session.token))
      .catch(() => {})
      .finally(() => setCheckingSession(false));
  }, [checkingSession]);

  /*
   * A super admin's selected account travels with every request; the
   * API checks it and ignores it for everyone else.
   */
  const selectedAccountId =
    currentUser?.role === "super_admin"
      ? managedAccountId
      : "";

  const selectedAccountIdRef = useRef("");
  selectedAccountIdRef.current = selectedAccountId;

  const authHeaders =
    useMemo(
      // ./session swaps in the newest token on each request.
      () => ({
        Authorization:
          `Bearer ${token}`,
        ...(selectedAccountId
          ? { "X-Account-Id": selectedAccountId }
          : {}),
      }),
      [token, selectedAccountId]
    );

  const isConversationAdmin =
    [
      "super_admin",
      "account_admin",
    ].includes(
      currentUser?.role
    );


  // =======================================================
  // PUSH ALERT TOGGLE
  // =======================================================

  const toggleNotifications =
    async () => {
      if (
        pushChanging ||
        !token
      ) {
        return;
      }

      setPushChanging(true);
      setError("");

      try {
        if (
          !(
            "serviceWorker" in
            navigator
          ) ||
          !(
            "PushManager" in
            window
          ) ||
          !(
            "Notification" in
            window
          )
        ) {
          throw new Error(
            "Push notifications are not supported in this browser."
          );
        }

        const existingSubscription =
          await getCurrentPushSubscription();

        // -----------------------------------------------
        // TURN ALERTS OFF FOR THIS DEVICE
        // -----------------------------------------------

        if (
          existingSubscription
        ) {
          const endpoint =
            existingSubscription.endpoint;

          const deleteResponse =
            await fetch(
              `${API_BASE}/push/subscribe`,
              {
                method:
                  "DELETE",

                headers: {
                  "Content-Type":
                    "application/json",

                  Authorization:
                    `Bearer ${getAccessToken()}`,
                },

                body:
                  JSON.stringify({
                    endpoint,
                  }),
              }
            );

          if (
            !deleteResponse.ok
          ) {
            throw new Error(
              "Could not remove the push subscription."
            );
          }

          await existingSubscription.unsubscribe();

          setPushEnabled(false);

          console.log(
            "Push notifications disabled for this device."
          );

          return;
        }

        // -----------------------------------------------
        // TURN ALERTS ON FOR THIS DEVICE
        // -----------------------------------------------

        const permission =
          await requestNotificationPermission();

        if (
          permission !==
          "granted"
        ) {
          if (
            permission ===
            "denied"
          ) {
            throw new Error(
              "Notifications are blocked in your browser settings."
            );
          }

          return;
        }

        await registerPushSubscription(
          token
        );

        setPushEnabled(true);

        playNotificationSound();

        console.log(
          "Push notifications enabled for this device."
        );
      } catch (err) {
        console.error(
          "Push notification toggle failed:",
          err
        );

        setError(
          err.message ||
            "Could not update notifications."
        );
      } finally {
        setPushChanging(false);
      }
    };


  // =======================================================
  // CURRENT DEVICE PUSH STATE
  // =======================================================

  useEffect(() => {
    if (!token) {
      setPushEnabled(false);
      return undefined;
    }

    let cancelled = false;

    const checkPushSubscription =
      async () => {
        try {
          const subscription =
            await getCurrentPushSubscription();

          if (cancelled) {
            return;
          }

          if (!subscription) {
            setPushEnabled(false);
            return;
          }

          /*
           * The browser has a push subscription.
           * Save/upsert it against the currently logged-in
           * user so the backend ownership remains synchronized.
           */
          const saveResponse =
            await fetch(
              `${API_BASE}/push/subscribe`,
              {
                method:
                  "POST",

                headers: {
                  "Content-Type":
                    "application/json",

                  Authorization:
                    `Bearer ${getAccessToken()}`,
                },

                body:
                  JSON.stringify(
                    subscription
                  ),
              }
            );

          if (
            !saveResponse.ok
          ) {
            throw new Error(
              "Could not synchronize push subscription."
            );
          }

          if (!cancelled) {
            setPushEnabled(true);
          }
        } catch (err) {
          console.warn(
            "Could not determine push subscription state:",
            err
          );

          if (!cancelled) {
            setPushEnabled(false);
          }
        }
      };

    checkPushSubscription();

    return () => {
      cancelled = true;
    };
  }, [token]);


  // =======================================================
  // USER / ACCOUNT DATA
  // =======================================================

  const loadAccountUsers =
    useCallback(
      async () => {
        if (
          !isConversationAdmin
        ) {
          setAccountUsers([]);
          return;
        }

        try {
const response = await axios.get(`${API_BASE}/users`, {
  headers: authHeaders,
  params:
    currentUser?.role === "super_admin" &&
    managedAccountId
      ? {
          accountId: managedAccountId,
        }
      : undefined,
});

          setAccountUsers(
            Array.isArray(
              response.data?.users
            )
              ? response.data.users
              : []
          );
        } catch (err) {
          console.error(
            "Account user load error:",
            err
          );

          setError(
            "Unable to load account users."
          );
        }
      },
[
  authHeaders,
  currentUser?.role,
  isConversationAdmin,
  managedAccountId,
]
    );

// =======================================================
// SUPER ADMIN - LOAD MANAGED CLIENT ACCOUNTS
// =======================================================

const loadManagedAccounts = useCallback(
  async () => {
    if (
      currentUser?.role !==
      "super_admin"
    ) {
      setManagedAccounts([]);
      setManagedAccountId("");
      return;
    }

    try {
      const response =
        await axios.get(
          `${API_BASE}/accounts`,
          {
            headers:
              authHeaders,
          }
        );

      const accounts =
        Array.isArray(
          response.data?.accounts
        )
          ? response.data.accounts
          : [];

      setManagedAccounts(
        accounts
      );

      /*
       * Default the super admin to their
       * own home account.
       */
      setManagedAccountId(
        (currentValue) =>
          currentValue ||
          currentUser?.accountId ||
          ""
      );
    } catch (err) {
      console.error(
        "Account list load error:",
        err
      );

      setError(
        err.response?.data?.error ||
          "Unable to load client accounts."
      );
    }
  },
[
  authHeaders,
  currentUser?.accountId,
  currentUser?.role,
]
);

useEffect(() => {
  if (
    token &&
    currentUser?.role ===
      "super_admin"
  ) {
    loadManagedAccounts();
  }
}, [
  token,
  currentUser?.role,
  loadManagedAccounts,
]);

useEffect(() => {
  return () => {
    if (selectedImagePreview) {
      URL.revokeObjectURL(
        selectedImagePreview
      );
    }
  };
}, [selectedImagePreview]);

  // =======================================================
  // VERIFY SESSION
  // =======================================================

  useEffect(() => {
    if (!token) {
      setCurrentUser(null);
      return undefined;
    }

    let cancelled = false;

    axios
      .get(
        `${API_BASE}/me`,
        {
          headers:
            authHeaders,
        }
      )
      .then(
        (response) => {
          if (
            !cancelled
          ) {
            setCurrentUser(
              response.data?.user ||
                null
            );
          }
        }
      )
      .catch(
        (err) => {
          if (
            cancelled
          ) {
            return;
          }

          if (
            err.response?.status ===
            401
          ) {
            setAccessToken("");

            setToken("");
            setCurrentUser(null);
            setConversations([]);
            setMessages([]);
            setActiveConv(null);
            setPushEnabled(false);
          } else {
            setError(
              "Unable to verify your session."
            );
          }
        }
      );

    return () => {
      cancelled = true;
    };
  }, [
    token,
    authHeaders,
  ]);

  useEffect(() => {
    if (
      token &&
      isConversationAdmin
    ) {
      loadAccountUsers();
    }
  }, [
    token,
    isConversationAdmin,
    loadAccountUsers,
  ]);

  useEffect(() => {
    activeConvRef.current =
      activeConv;
  }, [activeConv]);


  // =======================================================
  // LOAD CONVERSATIONS
  // =======================================================

  const loadConversations =
    useCallback(
      async () => {
        try {
          const res =
            await axios.get(
              `${API_BASE}/conversations`,
              {
                headers:
                  authHeaders,

params: {
  status: conversationStatus,

  ...(isConversationAdmin
    ? {
        assignedToUserId:
          conversationScope,
      }
    : {}),

  ...(currentUser?.role === "super_admin" &&
  managedAccountId
    ? {
        accountId: managedAccountId,
      }
    : {}),
},
              }
            );

          const data =
            res.data?.data ||
            res.data?.conversations ||
            res.data ||
            [];

          const conversationList =
            Array.isArray(data)
              ? data
              : [];

          setConversations(
            conversationList
          );

          if (
            activeConvRef.current &&
            !conversationList.some(
              (
                conversation
              ) =>
                conversation.id ===
                activeConvRef.current
            )
          ) {
            setActiveConv(null);
            setMessages([]);
          }
        } catch (err) {
          console.error(
            "Conversation load error:",
            err
          );

          setError(
            "Unable to load conversations."
          );
        }
      },
[
  authHeaders,
  conversationScope,
  conversationStatus,
  currentUser?.role,
  isConversationAdmin,
  managedAccountId,
]
    );


  // =======================================================
  // ASSIGN CONVERSATION
  // =======================================================

  const assignConversation =
    async (
      assignedToUserId
    ) => {
      if (
        !activeConv ||
        !isConversationAdmin ||
        assigningConversation
      ) {
        return;
      }

      setAssigningConversation(
        true
      );

      setError("");

      try {
        await axios.patch(
          `${API_BASE}/conversations/${activeConv}/assignment`,
          {
            assignedToUserId:
              assignedToUserId ||
              null,
          },
          {
            headers:
              authHeaders,
          }
        );

        await loadConversations();
      } catch (err) {
        console.error(
          "Conversation assignment error:",
          err
        );

        setError(
          err.response?.data?.error ||
            "The conversation could not be assigned."
        );
      } finally {
        setAssigningConversation(
          false
        );
      }
    };
	
const deleteConversation = async () => {
  if (
    !activeConv ||
    !isConversationAdmin
  ) {
    return;
  }

  const confirmed = window.confirm(
    "Permanently delete this conversation and all of its messages? This cannot be undone."
  );

  if (!confirmed) {
    return;
  }

  setError("");

  try {
    await axios.delete(
      `${API_BASE}/conversations/${activeConv}`,
      {
        headers: authHeaders,
      }
    );

    setActiveConv(null);
    setMessages([]);

    await loadConversations();
  } catch (err) {
    console.error(
      "Conversation delete error:",
      err
    );

    setError(
      err.response?.data?.error ||
        "The conversation could not be deleted."
    );
  }
};	

const archiveConversation = async () => {
  if (!activeConv) {
    return;
  }

  setError("");

  try {
    await axios.patch(
      `${API_BASE}/conversations/${activeConv}/archive`,
      {},
      {
        headers: authHeaders,
      }
    );

    setActiveConv(null);
    setMessages([]);

    await loadConversations();
  } catch (err) {
    console.error("Conversation archive error:", err);

    setError(
      err.response?.data?.error ||
        "The conversation could not be archived."
    );
  }
};

const restoreConversation = async () => {
  if (!activeConv) {
    return;
  }

  setError("");

  try {
    await axios.patch(
      `${API_BASE}/conversations/${activeConv}/restore`,
      {},
      {
        headers: authHeaders,
      }
    );

    setActiveConv(null);
    setMessages([]);

    await loadConversations();
  } catch (err) {
    console.error("Conversation restore error:", err);

    setError(
      err.response?.data?.error ||
        "The conversation could not be restored."
    );
  }
};

  // =======================================================
  // LOAD MESSAGES
  // =======================================================

  const loadMessages =
    useCallback(
      async (
        conversationId
      ) => {
        setActiveConv(
          conversationId
        );

        setLoadingMessages(
          true
        );

        setError("");

        try {
          const res =
            await axios.get(
              `${API_BASE}/conversations/${conversationId}/messages`,
              {
                headers:
                  authHeaders,
              }
            );

          setMessages(
            res.data?.messages ||
              []
          );

          await axios.post(
            `${API_BASE}/conversations/${conversationId}/read`,
            {},
            {
              headers:
                authHeaders,
            }
          );

          setConversations(
            (
              previousConversations
            ) =>
              previousConversations.map(
                (
                  conversation
                ) =>
                  conversation.id ===
                  conversationId
                    ? {
                        ...conversation,
                        unreadCount:
                          0,
                      }
                    : conversation
              )
          );
        } catch (err) {
          console.error(
            "Message load error:",
            err
          );

          setError(
            "Unable to load this conversation."
          );
        } finally {
          setLoadingMessages(
            false
          );
        }
      },
      [authHeaders]
    );


  // =======================================================
  // CREATE CONVERSATION
  // =======================================================

  /*
   * Admins choose which business number a new conversation uses;
   * it defaults to their own number when they have one.
   */
  useEffect(() => {
    if (!showNewConversation || !isConversationAdmin) {
      setBusinessNumbers([]);
      setNewConversationNumberId("");
      return undefined;
    }

    let cancelled = false;

    axios
      .get(`${API_BASE}/phone-numbers`, {
        headers: authHeaders,
      })
      .then((response) => {
        if (cancelled) {
          return;
        }

        // Released numbers can't send.
        const numbers = Array.isArray(response.data?.phoneNumbers)
          ? response.data.phoneNumbers.filter(
              (number) => number.status === "active"
            )
          : [];

        const ownNumber = numbers.find(
          (number) => number.assignedToUserId === currentUser?.id
        );

        setBusinessNumbers(numbers);
        setNewConversationNumberId(
          (ownNumber || numbers[0])?.id || ""
        );
      })
      .catch((err) => {
        console.error("Business number load error:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [
    authHeaders,
    currentUser?.id,
    isConversationAdmin,
    showNewConversation,
  ]);

  const createConversation =
    async () => {
      if (
        !newConversationPhone.trim() ||
        creatingConversation
      ) {
        return;
      }

      if (isConversationAdmin && !newConversationNumberId) {
        setError(
          "This account has no active numbers. Pick a client account that has one."
        );
        return;
      }

      setCreatingConversation(
        true
      );

      setError("");

      try {
        const response =
          await axios.post(
            `${API_BASE}/conversations`,
            {
              phoneNumber:
                newConversationPhone,

              firstName:
                newConversationFirstName,

              lastName:
                newConversationLastName,

              ...(newConversationNumberId
                ? {
                    phoneNumberId:
                      newConversationNumberId,
                  }
                : {}),
            },
            {
              headers:
                authHeaders,
            }
          );

        const conversation =
          response.data?.conversation;

        if (
          !conversation?.id
        ) {
          throw new Error(
            "Conversation was not returned by the API"
          );
        }

        setShowNewConversation(
          false
        );

        setNewConversationPhone(
          ""
        );

        setNewConversationFirstName(
          ""
        );

        setNewConversationLastName(
          ""
        );

        await loadConversations();
        await loadMessages(
          conversation.id
        );
      } catch (err) {
        console.error(
          "Conversation creation error:",
          err
        );

        setError(
          err.response?.data?.error ||
            "The conversation could not be created."
        );
      } finally {
        setCreatingConversation(
          false
        );
      }
    };


  // =======================================================
  // SEND MESSAGE
  // =======================================================

  const sendMessage =
    async () => {
      const body =
        input.trim();

      if (
        (
          !body &&
          !selectedImage
        ) ||
        !activeConv ||
        sending
      ) {
        return;
      }

      setSending(true);
      setError("");
      setUploadProgress(selectedImage ? 0 : null);
      // Always show the reader their own new message.
      stickToBottomRef.current = true;

      try {
        let media = null;

        if (selectedImage) {
          const formData =
            new FormData();

          formData.append(
            "file",
            selectedImage
          );

          const uploadRes =
            await axios.post(
              `${API_BASE}/media/upload`,
              formData,
              {
                headers: {
                  Authorization:
                    `Bearer ${getAccessToken()}`,
                },
                onUploadProgress: (progressEvent) => {
                  if (progressEvent.total) {
                    setUploadProgress(
                      Math.round(
                        (progressEvent.loaded * 100) /
                          progressEvent.total
                      )
                    );
                  }
                },
              }
            );

          media =
            uploadRes.data?.media;

          if (!media) {
            throw new Error(
              "Image upload did not return media information."
            );
          }
        }

        const messagePayload = {
          body,
          ...(media
            ? {
                media,
              }
            : {}),
        };

        const res =
          await axios.post(
            `${API_BASE}/conversations/${activeConv}/messages`,
            messagePayload,
            {
              headers:
                authHeaders,
            }
          );

        const returnedMessage =
          res.data?.message;

        if (
          returnedMessage
        ) {
          setMessages(
            (
              previousMessages
            ) => {
              const alreadyExists =
                previousMessages.some(
                  (
                    message
                  ) =>
                    message.id ===
                    returnedMessage.id
                );

              if (
                alreadyExists
              ) {
                return previousMessages;
              }

              return [
                ...previousMessages,
                returnedMessage,
              ];
            }
          );
        }

        setInput("");

        if (
          selectedImagePreview
        ) {
          URL.revokeObjectURL(
            selectedImagePreview
          );
        }

        setSelectedImage(
          null
        );

        setSelectedImagePreview(
          ""
        );

        loadConversations();
      } catch (err) {
        console.error(
          "Send error:",
          err
        );

        setError(
          err.response?.status === 413
            ? "That file is too large to send."
            : err.response?.data?.error ||
                err.message ||
                "The message could not be sent."
        );
      } finally {
        setSending(false);
        setUploadProgress(null);
      }
    };


  // =======================================================
// SOCKET.IO REAL-TIME EVENTS
// =======================================================

useEffect(() => {
  if (!token) {
    return undefined;
  }

  loadConversations();

  const socket = io(
    SOCKET_URL,
    {
      transports: [
        "polling",
        "websocket",
      ],

      // Read on every (re)connect, so it always has the newest token.
      auth: (send) =>
        send({
          token: getAccessToken(),
        }),
    }
  );

  // The server refused the token: refresh once, then reconnect.
  socket.on(
    "connect_error",
    (err) => {
      if (
        !socket.active &&
        err.message?.startsWith("Authentication error")
      ) {
        refreshSession()
          .then(() => socket.connect())
          .catch(() => {});
      }
    }
  );

  // -------------------------------------------------------
  // SOCKET CONNECT
  // -------------------------------------------------------

  socket.on(
    "connect",
    () => {
      console.log(
        "Socket connected:",
        socket.id
      );

      // Live updates for the account a super admin has selected.
      if (selectedAccountIdRef.current) {
        socket.emit(
          "account:select",
          selectedAccountIdRef.current
        );
      }

      /*
       * Re-sync the inbox after:
       *
       * - initial connection
       * - browser reconnect
       * - mobile reconnect
       */
      loadConversations();
    }
  );


  // -------------------------------------------------------
  // NEW MESSAGE
  // -------------------------------------------------------

  const handleNewMessage =
    async (data) => {
      if (!data?.message) {
        return;
      }

      const isActiveConversation =
        data.conversationId ===
        activeConvRef.current;

      /*
       * Play the in-browser notification sound only when:
       *
       * - Alerts are enabled on this device
       * - the inbound message belongs to another conversation
       */
      if (
        pushEnabled &&
        !isActiveConversation &&
        data.message.direction ===
          "inbound"
      ) {
        playNotificationSound();
      }

      /*
       * If the user currently has this conversation open,
       * append the new message to the chat immediately.
       */
      if (isActiveConversation) {
        setMessages(
          (
            previousMessages
          ) => {
            const alreadyExists =
              previousMessages.some(
                (
                  message
                ) =>
                  message.id ===
                  data.message.id
              );

            if (alreadyExists) {
              return previousMessages;
            }

            return [
              ...previousMessages,
              data.message,
            ];
          }
        );

        /*
         * Since the conversation is already open,
         * immediately mark the incoming message as read.
         */
        try {
          await axios.post(
            `${API_BASE}/conversations/${data.conversationId}/read`,
            {},
            {
              headers:
                authHeaders,
            }
          );
        } catch (err) {
          console.error(
            "Could not mark active conversation as read:",
            err
          );
        }
      }

      /*
       * Refresh the conversation list so:
       *
       * - previews update
       * - unread counts update
       * - ordering by lastMessageAt updates
       */
      await loadConversations();

      /*
       * Force unread count to zero for the conversation
       * currently being viewed.
       */
      if (isActiveConversation) {
        setConversations(
          (
            previousConversations
          ) =>
            previousConversations.map(
              (
                conversation
              ) =>
                conversation.id ===
                data.conversationId
                  ? {
                      ...conversation,
                      unreadCount: 0,
                    }
                  : conversation
            )
        );
      }
    };


  // -------------------------------------------------------
  // MESSAGE DELIVERY STATUS
  // -------------------------------------------------------

  const handleMessageStatus =
    (data) => {
      if (!data?.messageId) {
        return;
      }

      setMessages(
        (
          previousMessages
        ) =>
          previousMessages.map(
            (message) =>
              message.id ===
              data.messageId
                ? {
                    ...message,
                    status:
                      data.status,
                  }
                : message
          )
      );
    };


  // -------------------------------------------------------
  // CONVERSATION ASSIGNMENT
  // -------------------------------------------------------

  const handleConversationAssignment =
    (data) => {
      if (
        !data?.conversationId
      ) {
        return;
      }

      /*
       * If a non-admin user currently has this conversation
       * open and an administrator reassigns it to somebody
       * else, immediately close it on this user's screen.
       */
      if (
        !isConversationAdmin &&
        data.conversationId ===
          activeConvRef.current &&
        data.assignedToUserId !==
          currentUser?.id
      ) {
        setActiveConv(null);
        setMessages([]);
      }

      loadConversations();
    };


  // -------------------------------------------------------
  // CONVERSATION ARCHIVED
  // -------------------------------------------------------

  const handleConversationArchived =
    (data) => {
      if (
        !data?.conversationId
      ) {
        return;
      }

      /*
       * If this conversation is currently open,
       * close the chat immediately.
       */
      if (
        data.conversationId ===
        activeConvRef.current
      ) {
        setActiveConv(null);
        setMessages([]);
      }

      /*
       * Refresh whichever view is currently active.
       *
       * Active view:
       * archived conversation disappears.
       *
       * Archived view:
       * archived conversation appears.
       */
      loadConversations();
    };


  // -------------------------------------------------------
  // CONVERSATION RESTORED
  // -------------------------------------------------------

  const handleConversationRestored =
    (data) => {
      if (
        !data?.conversationId
      ) {
        return;
      }

      /*
       * Refresh whichever status view is currently open.
       *
       * Archived view:
       * restored conversation disappears.
       *
       * Active view:
       * restored conversation appears.
       */
      loadConversations();
    };


  // -------------------------------------------------------
  // CONVERSATION PERMANENTLY DELETED
  // -------------------------------------------------------

  const handleConversationDeleted =
    (data) => {
      if (
        !data?.conversationId
      ) {
        return;
      }

      /*
       * If another admin deletes the conversation while
       * this user is viewing it, immediately close it.
       */
      if (
        data.conversationId ===
        activeConvRef.current
      ) {
        setActiveConv(null);
        setMessages([]);
      }

      loadConversations();
    };


  // =======================================================
  // REGISTER SOCKET EVENTS
  // =======================================================

  socket.on(
    "message:new",
    handleNewMessage
  );

  socket.on(
    "message:status",
    handleMessageStatus
  );

  socket.on(
    "conversation:assignment",
    handleConversationAssignment
  );

  socket.on(
    "conversation:archived",
    handleConversationArchived
  );

  socket.on(
    "conversation:restored",
    handleConversationRestored
  );

  socket.on(
    "conversation:deleted",
    handleConversationDeleted
  );


  // -------------------------------------------------------
  // TEMPORARY COMPATIBILITY WITH OLDER EVENT NAMES
  // -------------------------------------------------------

  socket.on(
    "newMessage",
    handleNewMessage
  );

  socket.on(
    "messageStatus",
    handleMessageStatus
  );


  // -------------------------------------------------------
  // SOCKET DISCONNECT
  // -------------------------------------------------------

  socket.on(
    "disconnect",
    () => {
      console.log(
        "Socket disconnected"
      );
    }
  );


  // -------------------------------------------------------
  // CLEANUP
  // -------------------------------------------------------

  return () => {
    socket.disconnect();
  };
}, [
  authHeaders,
  currentUser?.id,
  isConversationAdmin,
  loadConversations,
  pushEnabled,
  token,
]);

  // =======================================================
  // AUTO-SCROLL
  // =======================================================

  // Opening a conversation always starts at the newest message.
  useEffect(() => {
    stickToBottomRef.current = true;
  }, [activeConv]);

  // New messages and status updates only scroll the thread when the
  // reader is already at the bottom, so reading older messages is
  // never interrupted.
  useEffect(() => {
    if (stickToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({
        block: "end",
      });
    }
  }, [messages, loadingMessages]);

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;

    if (!container) return;

    stickToBottomRef.current =
      container.scrollHeight -
        container.scrollTop -
        container.clientHeight <
      80;
  };

  // Pictures load after layout; keep the newest message in view.
  const handleMediaLoad = () => {
    if (stickToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({
        block: "end",
      });
    }
  };

  // Composer grows with its content up to a limit.
  useEffect(() => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [input, activeConv]);

  // Close popovers on outside click or Escape.
  useEffect(() => {
    const popovers = [
      [showEmojiPicker, emojiWrapperRef, setShowEmojiPicker],
      [showAccountMenu, accountMenuRef, setShowAccountMenu],
      [showConversationMenu, conversationMenuRef, setShowConversationMenu],
    ].filter(([open]) => open);

    if (popovers.length === 0) return undefined;

    const handlePointerDown = (event) => {
      popovers.forEach(([, ref, setOpen]) => {
        if (ref.current && !ref.current.contains(event.target)) {
          setOpen(false);
        }
      });
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        popovers.forEach(([, , setOpen]) => setOpen(false));
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showEmojiPicker, showAccountMenu, showConversationMenu]);

  useEffect(() => {
    setShowConversationMenu(false);
  }, [activeConv]);

  const clearSelectedImage = () => {
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
    }

    setSelectedImage(null);
    setSelectedImagePreview("");
  };

  const handleFileSelected = async (event) => {
    const pickedFile = event.target.files?.[0];

    event.target.value = "";

    if (!pickedFile) {
      return;
    }

    if (!ACCEPTED_MEDIA_TYPES.includes(pickedFile.type)) {
      setError(
        isVideoType(pickedFile.type)
          ? "Videos must be MP4 or 3GP."
          : "Pictures must be JPG, PNG, GIF or WebP. Videos must be MP4 or 3GP."
      );
      return;
    }

    // Videos can't be shrunk in the browser; carriers cap MMS near 1 MB.
    if (isVideoType(pickedFile.type) && pickedFile.size > MAX_IMAGE_BYTES) {
      setError(
        `That video is ${(pickedFile.size / 1024 / 1024).toFixed(1)} MB. Videos must be under 900 KB, which is a few seconds of phone video. Trim it and try again.`
      );
      return;
    }

    let file;

    try {
      file = await shrinkImage(pickedFile);
    } catch (shrinkError) {
      file = null;
    }

    if (!file) {
      setError(
        "That picture is too large to send. Pictures must be under 900 KB (GIFs can't be shrunk)."
      );
      return;
    }

    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
    }

    setSelectedImage(file);
    setSelectedImagePreview(URL.createObjectURL(file));
    setError("");
    textareaRef.current?.focus();
  };

  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? input.length;
    const end = textarea?.selectionEnd ?? input.length;

    setInput(
      (current) => `${current.slice(0, start)}${emoji}${current.slice(end)}`
    );
    setShowEmojiPicker(false);

    requestAnimationFrame(() => {
      if (textarea) {
        textarea.focus();
        textarea.selectionStart = start + emoji.length;
        textarea.selectionEnd = start + emoji.length;
      }
    });
  };


  // =======================================================
  // DERIVED DATA
  // =======================================================

  const activeConversation =
    conversations.find(
      (conversation) =>
        conversation.id ===
        activeConv
    );

  const filteredConversations =
    conversations.filter(
      (conversation) => {
        const phoneNumber =
          getPhoneNumber(
            conversation
          ).toLowerCase();

        const formattedPhoneNumber =
          formatPhoneNumber(
            getPhoneNumber(
              conversation
            )
          ).toLowerCase();

        const contactName =
          getContactName(
            conversation
          ).toLowerCase();

        const businessPhoneNumber =
          getBusinessPhoneNumber(
            conversation
          ).toLowerCase();

        const formattedBusinessPhoneNumber =
          formatPhoneNumber(
            getBusinessPhoneNumber(
              conversation
            )
          ).toLowerCase();

        const businessPhoneLabel =
          getBusinessPhoneLabel(
            conversation
          ).toLowerCase();

        const preview =
          conversation?.messages?.[0]
            ?.body ||
          conversation?.lastMessage
            ?.body ||
          conversation?.latestMessage
            ?.body ||
          "";

        const searchTerm =
          search
            .trim()
            .toLowerCase();

        return (
          phoneNumber.includes(
            searchTerm
          ) ||
          formattedPhoneNumber.includes(
            searchTerm
          ) ||
          contactName.includes(
            searchTerm
          ) ||
          businessPhoneNumber.includes(
            searchTerm
          ) ||
          formattedBusinessPhoneNumber.includes(
            searchTerm
          ) ||
          businessPhoneLabel.includes(
            searchTerm
          ) ||
          preview
            .toLowerCase()
            .includes(
              searchTerm
            )
        );
      }
    );


  // =======================================================
  // COMPOSER KEY HANDLER
  // =======================================================

  const handleComposerKeyDown =
    (event) => {
      if (
        event.key ===
          "Enter" &&
        !event.shiftKey
      ) {
        event.preventDefault();
        sendMessage();
      }
    };


  // =======================================================
  // LOGIN SCREEN
  // =======================================================

  if (checkingSession) {
    return null;
  }

  if (!token) {
    return (
      <Login
        onLogin={
          handleLogin
        }
      />
    );
  }


  // =======================================================
  // MAIN APPLICATION UI
  // =======================================================

  const isArchivedView = conversationStatus === "archived";

  const canManageUsers = ["super_admin", "account_admin"].includes(
    currentUser?.role
  );

  const currentUserName =
    `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
    currentUser?.email ||
    "Signed-in user";

  const currentUserInitials =
    `${currentUser?.firstName?.[0] || ""}${
      currentUser?.lastName?.[0] || ""
    }`.toUpperCase() ||
    currentUser?.email?.[0]?.toUpperCase() ||
    "?";

  const switchConversationStatus = (status) => {
    setConversationStatus(status);
    setActiveConv(null);
    setMessages([]);
  };

  const showConversationMenuButton =
    isConversationAdmin && isArchivedView;

  const activeContactName = getContactName(activeConversation);

  const activeCustomerNumber = formatPhoneNumber(
    getPhoneNumber(activeConversation)
  );

  return (
    <div className={activeConv ? "app-shell chat-open" : "app-shell"}>

      {/* =================== SIDEBAR =================== */}
      <aside className="sidebar" aria-label="Conversations">

        <div className="sidebar-top">
          <div className="brand">
            <img className="brand-logo" src={LOGO_URL} alt="ReachLink" />
          </div>

          <button
            type="button"
            className="button button-primary new-conversation-button"
            onClick={() => setShowNewConversation(true)}
            title="Start a new conversation"
            aria-label="New message"
          >
            <Icon name="compose" size={16} />
            <span>New message</span>
          </button>
        </div>

        <div className="sidebar-controls">
          <label className="search-field">
            <span className="visually-hidden">Search conversations</span>
            <Icon name="search" size={16} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, number or message"
            />
          </label>

          <div
            className="segmented-control"
            role="group"
            aria-label="Conversation status"
          >
            <button
              type="button"
              className={!isArchivedView ? "selected" : ""}
              aria-pressed={!isArchivedView}
              onClick={() => switchConversationStatus("open")}
            >
              Active
            </button>
            <button
              type="button"
              className={isArchivedView ? "selected" : ""}
              aria-pressed={isArchivedView}
              onClick={() => switchConversationStatus("archived")}
            >
              Archived
            </button>
          </div>

          {currentUser?.role === "super_admin" && (
            <div className="select-field">
              <label htmlFor="managed-account">Account</label>
              <select
                id="managed-account"
                value={managedAccountId}
                onChange={(event) => {
                  setManagedAccountId(event.target.value);
                  setConversationScope("all");
                  setActiveConv(null);
                  setMessages([]);
                }}
              >
                {managedAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isConversationAdmin && (
            <div className="select-field">
              <label htmlFor="conversation-scope">Inbox</label>
              <select
                id="conversation-scope"
                value={conversationScope}
                onChange={(event) => {
                  setConversationScope(event.target.value);
                  setActiveConv(null);
                  setMessages([]);
                }}
              >
                <option value="all">All conversations</option>
                <option value="unassigned">Unassigned</option>
                {accountUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                    {user.status !== "active" ? " (Inactive)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="conversation-list-header">
          {filteredConversations.length}{" "}
          {isArchivedView ? "archived" : "active"}{" "}
          {filteredConversations.length === 1
            ? "conversation"
            : "conversations"}
        </div>

        <nav className="conversation-list" aria-label="Conversation list">
          {filteredConversations.length > 0 ? (
            filteredConversations.map((conversation) => {
              const selected = conversation.id === activeConv;
              const contactName = getContactName(conversation);
              const customerNumber = formatPhoneNumber(
                getPhoneNumber(conversation)
              );
              const businessPhoneNumber = getBusinessPhoneNumber(
                conversation
              );
              const businessPhoneLabel = getBusinessPhoneLabel(
                conversation
              );
              const unreadCount = Number(conversation?.unreadCount || 0);

              const previewMessage =
                conversation?.messages?.[0] ||
                conversation?.lastMessage ||
                conversation?.latestMessage ||
                null;

              const hasPicture =
                Array.isArray(previewMessage?.media) &&
                previewMessage.media.length > 0;

              const hasVideo =
                hasPicture &&
                previewMessage.media.some((item) =>
                  isVideoType(item.contentType)
                );

              const preview =
                previewMessage?.body ||
                (hasVideo
                  ? "Video"
                  : hasPicture
                    ? "Picture"
                    : "No messages yet");

              const previewPrefix =
                previewMessage?.direction === "outbound" ? "You: " : "";

              return (
                <button
                  type="button"
                  key={conversation.id}
                  className={[
                    "conversation-item",
                    selected ? "selected" : "",
                    unreadCount > 0 ? "unread" : "",
                  ].join(" ")}
                  aria-current={selected ? "true" : undefined}
                  onClick={() => loadMessages(conversation.id)}
                >
                  <span className="avatar" aria-hidden="true">
                    {contactName ? (
                      getInitials(conversation)
                    ) : (
                      <Icon name="user" size={18} />
                    )}
                  </span>

                  <span className="conversation-content">
                    <span className="conversation-row">
                      <span className="conversation-name">
                        {contactName || customerNumber}
                      </span>
                      <span className="conversation-time">
                        {formatListTime(conversation.lastMessageAt)}
                      </span>
                    </span>

                    <span className="conversation-row">
                      <span className="conversation-preview">
                        {hasPicture && !previewMessage?.body && (
                          <Icon name={hasVideo ? "video" : "image"} size={14} />
                        )}
                        <span className="preview-text">
                          {previewPrefix}
                          {preview}
                        </span>
                      </span>
                      {unreadCount > 0 && (
                        <span
                          className="unread-badge"
                          aria-label={`${unreadCount} unread`}
                        >
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </span>

                    {(contactName || businessPhoneNumber) && (
                      <span className="conversation-meta">
                        {contactName && (
                          <span className="conversation-customer-number">
                            {customerNumber}
                          </span>
                        )}
                        {businessPhoneNumber && (
                          <span
                            className="business-line-tag"
                            title={`Our number: ${
                              businessPhoneLabel || "Business line"
                            } ${formatPhoneNumber(businessPhoneNumber)}`}
                          >
                            via {businessPhoneLabel || "Business line"} ·{" "}
                            {formatPhoneNumber(businessPhoneNumber)}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="empty-conversation-list">
              {search.trim()
                ? `No conversations match "${search.trim()}".`
                : isArchivedView
                  ? "No archived conversations."
                  : "No active conversations."}
            </div>
          )}
        </nav>

        {/* ACCOUNT */}
        <div className="sidebar-account" ref={accountMenuRef}>
          {showAccountMenu && (
            <div className="menu account-menu" role="menu">
              <div className="menu-heading">
                <strong>{currentUserName}</strong>
                <span>{currentUser?.email}</span>
              </div>

              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={pushEnabled}
                className="menu-item notification-toggle"
                onClick={toggleNotifications}
                disabled={pushChanging}
              >
                <Icon name={pushEnabled ? "bell" : "bellOff"} />
                <span className="menu-item-text">
                  Notifications on this device
                  <small>
                    {pushChanging
                      ? "Updating…"
                      : pushEnabled
                        ? "On. You'll be alerted about new messages."
                        : "Off. Turn on to be alerted about new messages."}
                  </small>
                </span>
                <span
                  className={pushEnabled ? "switch on" : "switch"}
                  aria-hidden="true"
                />
              </button>

              {currentUser?.role === "super_admin" && (
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    setShowAccountMenu(false);
                    setShowAdminPanel(true);
                  }}
                >
                  <Icon name="building" />
                  <span className="menu-item-text">Clients</span>
                </button>
              )}

              {canManageUsers && (
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    setShowAccountMenu(false);
                    setShowUserManagement(true);
                  }}
                >
                  <Icon name="users" />
                  <span className="menu-item-text">Manage users</span>
                </button>
              )}

              <div className="menu-divider" />

              <button
                type="button"
                role="menuitem"
                className="menu-item"
                onClick={handleLogout}
              >
                <Icon name="logout" />
                <span className="menu-item-text">Sign out</span>
              </button>
            </div>
          )}

          <button
            type="button"
            className="account-button"
            aria-haspopup="menu"
            aria-expanded={showAccountMenu}
            onClick={() => setShowAccountMenu((current) => !current)}
          >
            <span className="avatar avatar-dark" aria-hidden="true">
              {currentUserInitials}
            </span>
            <span className="account-copy">
              <strong>{currentUserName}</strong>
              <span>{currentUser?.account?.name || "10 Digits"}</span>
            </span>
            <span
              className={
                pushEnabled
                  ? "notification-status on"
                  : "notification-status"
              }
              title={
                pushEnabled
                  ? "Notifications are on for this device"
                  : "Notifications are off for this device"
              }
            >
              <Icon name={pushEnabled ? "bell" : "bellOff"} size={16} />
              <span className="visually-hidden">
                {pushEnabled ? "Notifications on" : "Notifications off"}
              </span>
            </span>
            <Icon name="chevronUp" size={16} />
          </button>
        </div>
      </aside>

      {/* =================== CHAT =================== */}
      <main className="chat-panel">
        {!activeConv ? (
          <div className="empty-chat">
            <div className="empty-chat-icon">
              <Icon name="message" size={28} />
            </div>
            <h2>Your messages</h2>
            <p>Select a conversation, or start a new one.</p>
          </div>
        ) : (
          <>
            <header className="chat-header">
              <button
                type="button"
                className="icon-button mobile-back-button"
                onClick={() => setActiveConv(null)}
                aria-label="Back to conversations"
              >
                <Icon name="back" size={20} />
              </button>

              <div className="chat-contact">
                <span className="avatar avatar-large" aria-hidden="true">
                  {activeContactName ? (
                    getInitials(activeConversation)
                  ) : (
                    <Icon name="user" size={20} />
                  )}
                </span>

                <div className="chat-contact-copy">
                  <h2>
                    {activeContactName || activeCustomerNumber}
                    {isArchivedView && (
                      <span className="status-pill">Archived</span>
                    )}
                  </h2>

                  <div className="chat-contact-details">
                    {activeContactName && (
                      <span>{activeCustomerNumber}</span>
                    )}

                    {getBusinessPhoneNumber(activeConversation) && (
                      <span className="business-line-tag">
                        Texting from{" "}
                        {getBusinessPhoneLabel(activeConversation) ||
                          "Business line"}{" "}
                        ·{" "}
                        {formatPhoneNumber(
                          getBusinessPhoneNumber(activeConversation)
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="chat-header-actions">
                {isConversationAdmin && activeConversation && (
                  <div className="assignment-control">
                    <label htmlFor="conversation-assignment">
                      Assigned to
                    </label>
                    <select
                      id="conversation-assignment"
                      value={activeConversation.assignedToUserId || ""}
                      disabled={assigningConversation}
                      onChange={(event) =>
                        assignConversation(event.target.value)
                      }
                    >
                      <option value="">Unassigned</option>
                      {accountUsers
                        .filter((user) => user.status === "active")
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.firstName} {user.lastName}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <button
                  type="button"
                  className="button button-secondary"
                  onClick={
                    isArchivedView
                      ? restoreConversation
                      : archiveConversation
                  }
                  title={
                    isArchivedView
                      ? "Move back to active conversations"
                      : "Move to archived conversations"
                  }
                  aria-label={
                    isArchivedView
                      ? "Restore conversation"
                      : "Archive conversation"
                  }
                >
                  <Icon
                    name={isArchivedView ? "restore" : "archive"}
                    size={16}
                  />
                  <span className="button-label">
                    {isArchivedView ? "Restore" : "Archive"}
                  </span>
                </button>

                {showConversationMenuButton && (
                  <div className="menu-anchor" ref={conversationMenuRef}>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label="More conversation actions"
                      title="More actions"
                      aria-haspopup="menu"
                      aria-expanded={showConversationMenu}
                      onClick={() =>
                        setShowConversationMenu((current) => !current)
                      }
                    >
                      <Icon name="more" size={20} />
                    </button>

                    {showConversationMenu && (
                      <div className="menu conversation-menu" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className="menu-item danger"
                          onClick={() => {
                            setShowConversationMenu(false);
                            deleteConversation();
                          }}
                        >
                          <Icon name="trash" />
                          <span className="menu-item-text">
                            Delete permanently
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </header>

            {error && (
              <div className="error-banner" role="alert">
                <Icon name="alert" size={18} />
                <span>{error}</span>
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Dismiss error"
                  onClick={() => setError("")}
                >
                  <Icon name="close" size={16} />
                </button>
              </div>
            )}

            <section
              className="messages-container"
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
              aria-label="Messages"
            >
              {loadingMessages ? (
                <div className="thread-notice">Loading messages…</div>
              ) : messages.length > 0 ? (
                messages.map((message, index) => {
                  const previousMessage = messages[index - 1];
                  const nextMessage = messages[index + 1];

                  const showDate =
                    !previousMessage ||
                    new Date(message.createdAt).toDateString() !==
                      new Date(previousMessage.createdAt).toDateString();

                  const groupedWithPrevious =
                    !showDate && isSameGroup(previousMessage, message);
                  const groupedWithNext = isSameGroup(
                    message,
                    nextMessage
                  );

                  const outbound = message.direction === "outbound";
                  const status = outbound
                    ? getStatusLabel(message.status)
                    : "";
                  const failed = status === "Failed";
                  const hasMedia =
                    Array.isArray(message.media) &&
                    message.media.length > 0;
                  const showDetails = !groupedWithNext || failed;

                  return (
                    <React.Fragment key={message.id}>
                      {showDate && (
                        <div className="date-separator">
                          <span>{formatDate(message.createdAt)}</span>
                        </div>
                      )}

                      <div
                        className={[
                          "message-row",
                          outbound ? "outbound-row" : "inbound-row",
                          groupedWithPrevious ? "grouped" : "",
                        ].join(" ")}
                      >
                        <div
                          className={[
                            "message-bubble",
                            outbound ? "outbound-bubble" : "inbound-bubble",
                            failed ? "failed-message" : "",
                            hasMedia && !message.body ? "media-only" : "",
                            groupedWithNext ? "continues" : "",
                          ].join(" ")}
                          title={new Date(message.createdAt).toLocaleString()}
                        >
                          {hasMedia && (
                            <div className="message-media">
                              {message.media.map((mediaItem) => (
                                <MessageMediaItem
                                  key={mediaItem.id || mediaItem.url}
                                  item={mediaItem}
                                  onImageLoad={handleMediaLoad}
                                />
                              ))}
                            </div>
                          )}

                          {message.body && (
                            <div className="message-text">
                              {message.body}
                            </div>
                          )}

                          {!message.body && !hasMedia && (
                            <div className="message-text message-empty">
                              No text or attachment
                            </div>
                          )}
                        </div>

                        {showDetails && (
                          <div
                            className={
                              failed
                                ? "message-details failed-status"
                                : "message-details"
                            }
                          >
                            {failed && <Icon name="alert" size={13} />}
                            <span>{formatTime(message.createdAt)}</span>
                            {status && (
                              <>
                                <span aria-hidden="true">·</span>
                                <span>
                                  {failed ? "Not delivered" : status}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })
              ) : (
                <div className="thread-notice">
                  No messages yet. Send the first one below.
                </div>
              )}

              <div ref={messagesEndRef} />
            </section>

            {/* COMPOSER */}
            <footer className="composer-section">
              <div className="composer-box">
                {selectedImage && (
                  <div className="attachment-preview">
                    {isVideoType(selectedImage.type) ? (
                      <video
                        src={selectedImagePreview}
                        className="attachment-thumb"
                        muted
                        playsInline
                        preload="metadata"
                        aria-hidden="true"
                      />
                    ) : (
                      <img
                        src={selectedImagePreview}
                        alt=""
                        className="attachment-thumb"
                      />
                    )}
                    <div className="attachment-details">
                      <strong>{selectedImage.name}</strong>
                      {sending && uploadProgress !== null ? (
                        <span
                          className="upload-progress"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={uploadProgress}
                          aria-label="Upload progress"
                        >
                          <span style={{ width: `${uploadProgress}%` }} />
                        </span>
                      ) : (
                        <span>
                          {Math.max(1, Math.round(selectedImage.size / 1024))}{" "}
                          KB
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label="Remove attachment"
                      title="Remove attachment"
                      onClick={clearSelectedImage}
                      disabled={sending}
                    >
                      <Icon name="close" size={16} />
                    </button>
                  </div>
                )}

                <div className="composer-row">
                  <div className="menu-anchor" ref={emojiWrapperRef}>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label="Insert emoji"
                      title="Insert emoji"
                      aria-haspopup="true"
                      aria-expanded={showEmojiPicker}
                      onClick={() =>
                        setShowEmojiPicker((current) => !current)
                      }
                    >
                      <Icon name="smile" size={20} />
                    </button>

                    {showEmojiPicker && (
                      <div
                        className="emoji-picker"
                        role="group"
                        aria-label="Emoji"
                      >
                        {EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            className="emoji-picker-button"
                            onClick={() => insertEmoji(emoji)}
                            aria-label={`Insert ${emoji}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Attach a picture or short video"
                    title="Attach a picture (JPG, PNG, GIF, WebP) or a short video (MP4, 3GP, under 900 KB)"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                  >
                    <Icon name="paperclip" size={20} />
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_MEDIA_TYPES.join(",")}
                    className="visually-hidden"
                    tabIndex={-1}
                    onChange={handleFileSelected}
                  />

                  <label className="visually-hidden" htmlFor="composer-input">
                    Message
                  </label>
                  <textarea
                    id="composer-input"
                    ref={textareaRef}
                    rows={1}
                    maxLength={1600}
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={handleComposerKeyDown}
                    placeholder={
                      selectedImage ? "Add a message (optional)" : "Type a message"
                    }
                  />

                  <button
                    type="button"
                    className="button button-primary send-button"
                    onClick={sendMessage}
                    disabled={(!input.trim() && !selectedImage) || sending}
                    aria-label={sending ? "Sending" : "Send message"}
                  >
                    <Icon name="send" size={16} />
                    <span className="button-label">
                      {sending ? "Sending…" : "Send"}
                    </span>
                  </button>
                </div>
              </div>

              <div className="composer-hint">
                <span>Enter to send · Shift+Enter for a new line</span>
                {input.length > 0 && <span>{input.length} / 1600</span>}
              </div>
            </footer>
          </>
        )}
      </main>

      {/* NEW CONVERSATION */}
      {showNewConversation && (
        <div
          className="modal-overlay"
          onKeyDown={(event) => {
            if (event.key === "Escape") setShowNewConversation(false);
          }}
        >
          <div
            className="new-conversation-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-conversation-title"
          >
            <div className="new-conversation-header">
              <div>
                <h2 id="new-conversation-title">New message</h2>
                <p>Start a text conversation with a customer.</p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Close"
                onClick={() => setShowNewConversation(false)}
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            <div className="new-conversation-body">
              {error && (
                <div className="error-banner inline" role="alert">
                  <Icon name="alert" size={18} />
                  <span>{error}</span>
                </div>
              )}

              <label>
                Customer phone number
                <input
                  type="tel"
                  autoFocus
                  value={newConversationPhone}
                  onChange={(event) =>
                    setNewConversationPhone(event.target.value)
                  }
                  placeholder="(281) 555-1212"
                />
              </label>

              {businessNumbers.length > 1 && (
                <label>
                  Send from
                  <select
                    value={newConversationNumberId}
                    onChange={(event) =>
                      setNewConversationNumberId(event.target.value)
                    }
                  >
                    {businessNumbers.map((number) => (
                      <option key={number.id} value={number.id}>
                        {number.label
                          ? `${number.label} (${formatPhoneNumber(number.number)})`
                          : formatPhoneNumber(number.number)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="new-conversation-name-row">
                <label>
                  <span>
                    First name <span className="optional">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={newConversationFirstName}
                    onChange={(event) =>
                      setNewConversationFirstName(event.target.value)
                    }
                  />
                </label>
                <label>
                  <span>
                    Last name <span className="optional">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={newConversationLastName}
                    onChange={(event) =>
                      setNewConversationLastName(event.target.value)
                    }
                  />
                </label>
              </div>
            </div>

            <div className="new-conversation-footer">
              <button
                type="button"
                className="button button-secondary"
                onClick={() => setShowNewConversation(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={createConversation}
                disabled={
                  !newConversationPhone.trim() || creatingConversation
                }
              >
                {creatingConversation ? "Starting…" : "Start conversation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdminPanel && (
        <AdminPanel
          token={token}
          currentUser={currentUser}
          onClose={() => setShowAdminPanel(false)}
          onAccountsChanged={loadManagedAccounts}
        />
      )}

      {showUserManagement && (
        <UserManagement
          token={token}
          currentUser={currentUser}
          accountId={selectedAccountId}
          accountName={
            managedAccounts.find(
              (account) => account.id === selectedAccountId
            )?.name || ""
          }
          onClose={() => {
            setShowUserManagement(false);
            loadAccountUsers();
          }}
          onCurrentUserUpdated={(updatedUser) =>
            setCurrentUser((existingUser) => ({
              ...existingUser,
              ...updatedUser,
              account: existingUser?.account,
            }))
          }
        />
      )}
    </div>
  );
}

export default App;